import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Product} from '../../types/product';
import {useProducts} from '../../hooks/useProducts';
import {toggleFavorite} from '../../api/productApi';
import './OtherCourses.scss';

interface OtherCoursesProps {
    currentProductId?: string;
}

export const OtherCourses: React.FC<OtherCoursesProps> = ({currentProductId}) => {
    const navigate = useNavigate();
    const {products: allProducts, loading, error} = useProducts();
    const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
    const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]);
    const [isUpdatingFavorite, setIsUpdatingFavorite] = useState(false);

    useEffect(() => {
        if (allProducts && allProducts.length > 0) {
            const filtered = currentProductId
                ? allProducts.filter(p => p.id !== currentProductId)
                : [...allProducts];
            setAvailableProducts(filtered);

            const randomProducts = [...filtered]
                .sort(() => 0.5 - Math.random())
                .slice(0, 2);
            setDisplayedProducts(randomProducts);
        }
    }, [allProducts, currentProductId]);

    const handleToggleFavorite = async (productId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (isUpdatingFavorite) return;

        try {
            setIsUpdatingFavorite(true);
            await toggleFavorite(productId);
            setDisplayedProducts(prev =>
                prev.map(p =>
                    p.id === productId ? {...p, isFavorite: !p.isFavorite} : p
                )
            );
        } catch (error) {
            console.error('Ошибка при обновлении избранного:', error);
        } finally {
            setIsUpdatingFavorite(false);
        }
    };

    const handleCardClick = (productId: string) => {
        const remainingDisplayed = displayedProducts.filter(p => p.id !== productId);
        const remainingAvailable = availableProducts.filter(p =>
            !displayedProducts.some(dp => dp.id === p.id) && p.id !== productId
        );

        if (remainingAvailable.length > 0) {
            const randomIndex = Math.floor(Math.random() * remainingAvailable.length);
            const newProduct = remainingAvailable[randomIndex];
            setDisplayedProducts([...remainingDisplayed, newProduct]);
        }

        navigate(`/product/${productId}`);
    };

    const pluralizeReviews = (count: number): string => {
        if (count % 10 === 1 && count % 100 !== 11) return 'отзыв';
        if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'отзыва';
        return 'отзывов';
    };

    if (loading) return <div className="other-courses-loading">Загрузка...</div>;
    if (error) return <div className="other-courses-error">Ошибка загрузки</div>;
    if (availableProducts.length === 0) return null;

    return (
        <div className="other-courses-container">
            <h2 className="other-courses-title">Другие курсы</h2>

            <div className="courses-row">
                {displayedProducts
                    .filter(product => !product.purchased)
                    .map(product => (
                    <div
                        key={product.id}
                        className="course-card"
                        onClick={() => handleCardClick(product.id)}
                    >
                        <div className="image-container">
                            <img
                                src={product.image || ''}
                                alt={product.title}
                                className="course-image"
                            />

                            {product.discount && !product.purchased && (
                                <div className="discount-badge">
                                    <svg width="87" height="32" viewBox="0 0 87 32" fill="none">
                                        <path
                                            d="M0.5 12C0.5 5.37258 5.87258 0 12.5 0H74.5C81.1274 0 86.5 5.37258 86.5 12V32H0.5V12Z"
                                            fill="#FB034F"/>
                                        <path
                                            d="M22.18 17.704H24.756C23.828 19.864 21.732 21.208 19.3 21.208C15.844 21.208 13.076 18.472 13.076 15.048C13.076 11.704 15.812 8.968 19.156 8.968C21.668 8.968 23.732 10.296 24.756 12.568H22.308C21.508 11.528 20.58 11.08 19.204 11.08C16.964 11.08 15.284 12.792 15.284 15.064C15.284 17.288 17.076 19.096 19.252 19.096C20.388 19.096 21.204 18.712 22.18 17.704ZM26.1921 21V12.136H28.3201V16.216L31.3921 12.136H33.9681L30.4321 16.536L34.1601 21H31.3921L28.3201 16.92V21H26.1921ZM35.0515 21V12.136H37.1635V17.992L41.1315 12.136H43.2915V21H41.1795V15.144L37.2115 21H35.0515ZM44.3163 23.112V19.064H45.6763L48.6363 12.136H50.4923L53.4523 19.064H54.8123V23.112H53.2923L52.8443 21H46.3003L45.8523 23.112H44.3163ZM47.8843 19.064H51.2443L49.5643 15.064L47.8843 19.064ZM55.8328 21V12.136H57.9608V16.216L61.0328 12.136H63.6088L60.0728 16.536L63.8008 21H61.0328L57.9608 16.92V21H55.8328ZM64.2761 16.6C64.2761 13.896 66.1641 11.928 68.7881 11.928C70.0681 11.928 70.9481 12.344 71.6841 13.32V12.136H73.6361V21H71.6841V19.848C70.8361 20.824 70.0041 21.208 68.7401 21.208C66.1481 21.208 64.2761 19.272 64.2761 16.6ZM66.4201 16.648C66.4201 18.2 67.4921 19.256 69.0441 19.256C70.5801 19.256 71.6201 18.184 71.6201 16.6C71.6201 15 70.5321 13.88 68.9801 13.88C67.4921 13.88 66.4201 15.048 66.4201 16.648Z"
                                            fill="white"/>
                                    </svg>
                                </div>
                            )}

                            <button
                                className={`favorite-button ${product.isFavorite ? 'active' : ''}`}
                                onClick={(e) => handleToggleFavorite(product.id, e)}
                                disabled={isUpdatingFavorite}
                            >
                                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                                    <rect width="40" height="40" rx="12" fill="white"/>
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M23.7 12C26.87 12 29 14.98 29 17.76C29 23.39 20.16 28 20 28C19.84 28 11 23.39 11 17.76C11 14.98 13.13 12 16.3 12C18.12 12 19.31 12.91 20 13.71C20.69 12.91 21.88 12 23.7 12Z"
                                        fill={product.isFavorite ? "#FB034F" : "none"}
                                        stroke={product.isFavorite ? "none" : "#000000"}
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="price-section">
                            {product.discount ? (
                                <>
                                    <span className="discount-price">{product.discount} ₽</span>
                                    <span className="original-price">{product.price} ₽</span>
                                </>
                            ) : (
                                <span
                                    className="normal-price"> {product.price === 0 ? 'Бесплатно' : `${product.price} ₽`}
                                </span>
                            )}
                        </div>

                        {/*<p className="video-count">за {product.videosCount} видео</p>*/}

                        <h3 className="course-name">{product.title}</h3>

                        <div className="rating-section">
                            <span className="rating-star">★</span>
                            <span className="rating-value">{product.rating}</span>
                            <span className="reviews-count">
                               {product.reviewsCount === 0 ? "Нет отзывов"
                                   : `${product.reviewsCount} ${pluralizeReviews(product.reviewsCount)}`
                               }
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};